'use strict';

import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import moment from 'moment';
import redirectURL from '../redirectURL';
import { getHyphenDDMMMYYYYHHMM, getHyphenDDMMYYYY } from '../common/utils';
import TransporterDashboardModern from './transporterDashboardModern';
import FtlMovementDashboardModern from './ftlMovementDashboardModern';
import InboundDashboardModern from './inboundDashboardModern';
import PodDashboardModern from './podDashboardModern';
import Co2DashboardModern from './co2DashboardModern';
import {
  COLORS, MULTI_SELECT_STYLES, safeArray, groupBy, distinctValues,
  average, sum, round1, statusColorForPercent, statusClassForPercent,
  percentValueFormatter, percentCellClass, baseChartOptions, baseAxisLine, baseValueAxis,
  chartScrollWidth, buildPivot, GridCard,
} from './dashboardShared';
import './performanceDashboardModern.css';

function BarChartIcon() {
  return (
    <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="13" width="4.2" height="8" rx="1.1" fill="#2a78d6" />
      <rect x="9.9" y="8" width="4.2" height="13" rx="1.1" fill="#eb6834" />
      <rect x="16.8" y="4" width="4.2" height="17" rx="1.1" fill="#1baf7a" />
      <path d="M3.4 9.6 8.6 5.4l4 2.6L19 3" stroke="#0d366b" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="1.4" y="7.8" width="12" height="8.4" rx="1.3" fill="#1c3d6b" />
      <rect x="3" y="9.8" width="5.6" height="2.3" rx="0.6" fill="#eb6834" />
      <path d="M13.4 11h4.5a1.4 1.4 0 0 1 1.13.57l1.9 2.53c.19.25.29.56.29.87V16a1 1 0 0 1-1 1h-6.8v-6Z" fill="#eb6834" />
      <path d="M14.2 12.1h2.9l1.4 1.9h-4.3Z" fill="#fcd9c4" />
      <circle cx="6.4" cy="17.4" r="1.9" fill="#22314a" />
      <circle cx="6.4" cy="17.4" r="0.75" fill="#cbd5e1" />
      <circle cx="16.9" cy="17.4" r="1.9" fill="#22314a" />
      <circle cx="16.9" cy="17.4" r="0.75" fill="#cbd5e1" />
    </svg>
  );
}

function GaugeIcon() {
  return (
    <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.4 15.6V9a1.3 1.3 0 0 1 1.3-1.3h7.3A1.3 1.3 0 0 1 12.3 9v6.6" stroke="#0f9d68" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
      <path d="M12.3 11.1H16a1.3 1.3 0 0 1 1.04.52l1.66 2.2c.16.21.26.47.26.74v1a1 1 0 0 1-1 1h-5.66Z" stroke="#0f9d68" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
      <circle cx="6.1" cy="17.3" r="1.7" fill="#0f9d68" />
      <circle cx="15.5" cy="17.3" r="1.7" fill="#0f9d68" />
      <path d="M0.8 6.3h3.4M0.4 8.5h2.6M0.8 4.1h4.4" stroke="#7fe0bb" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function InboundIcon() {
  return (
    <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 11 12 4l9 7" stroke="#4a3aa7" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 10.3V19a1 1 0 0 0 1 1h4v-5h4v5h4a1 1 0 0 0 1-1v-8.7" fill="#e7e1fb" stroke="#4a3aa7" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M12 9.2v5.4" stroke="#eb6834" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9.5 12.3 12 15l2.5-2.7" stroke="#eb6834" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PodIcon() {
  return (
    <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.5" y="3.3" width="15" height="18.4" rx="2.2" fill="#fdeef4" stroke="#e87ba4" strokeWidth="1.4" />
      <rect x="8.4" y="2.2" width="7.2" height="3" rx="1" fill="#e87ba4" />
      <circle cx="12" cy="13.4" r="5" fill="#ffffff" stroke="#2a78d6" strokeWidth="1.5" />
      <path d="M9.6 13.5 11.3 15.2l3.1-3.6" stroke="#0f9d68" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LeafIcon() {
  return (
    <svg width="23" height="23" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9.4" fill="#eafaf0" stroke="#0f9d68" strokeWidth="1.6" />
      <path d="M7.9 15.1c-0.85-4.4 1.35-8.1 7.8-8.4 0.7 6.6-3.5 8.8-7.8 8.4Z" fill="#3fbf7f" stroke="#0f9d68" strokeWidth="1" strokeLinejoin="round" />
      <path d="M8.5 14.7c1.75-2.3 3.9-4.5 6.8-6.1" stroke="#0f9d68" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/*
 * Modern rebuild of the "Performance Dashboard" tab (Phase 1 of the 7-tab
 * logistics dashboard).
 *
 * Sourced from consignments/filterCombinedConsignmentsOptimized - the exact
 * same 4 raw Mongo collections (consignment_truck_level/consignments for FTL,
 * courier_cons_lr_no_wise/courierconsignments for PTL) that back the legacy
 * "Main Projects" screen's All tab (consolidatedScreen.js), just via a
 * concurrent-query/indexed twin of filterCombinedConsignments that returns
 * byte-for-byte identical data for identical params (see that handler's own
 * doc comment in consignmentsController.js). This dashboard previously read
 * consignments/getDataForPowerBiDashBoard (a thin proxy to a separate
 * external ML service), which turned out to carry roughly 1/3 the row count
 * of the live All tab for the same date range - not a logic bug, a genuinely
 * smaller/differently-scoped dataset - so matching the All tab's counts
 * exactly required switching the data source itself, not just the formulas
 * below (which already mirrored the All tab's logic before the switch).
 * Called with tabType: "all" (no server-side status exclusion - the
 * eligibility filter in computeDerived() applies the same all_tripsar rule
 * the All tab itself applies client-side) and data_type: "Total" (returns
 * every row in range, before any bucket-style post-filter).
 *
 * Delivered/On-Time definitions match the All tab's fragmentData exactly:
 *   - Delivered: consignment_status === "Delivered" (exact, PTL) OR numeric
 *     status in [5, 8] (FTL only - PTL rows never carry a numeric `status`
 *     field on this source, so the OR is naturally a no-op there; kept as an
 *     explicit FTL guard rather than relying on that omission). Deliberately
 *     narrower than the shared isDeliveredRow() helper used elsewhere on this
 *     app, which also treats "trip completed" as delivered - that's correct
 *     for Transporter/POD, which weren't part of this mismatch and use a
 *     different tab's/screen's convention, so it's kept local to this file
 *     rather than changed in dashboardShared.js.
 *   - On-Time: ontime_status === 1 AND transit_delay === 0 (both real fields
 *     on every row from this source, FTL and PTL alike). The All tab has a
 *     third override, sla_considered === "yes", but that field does not
 *     appear anywhere in this source's real sample data, so it's omitted
 *     rather than referencing a field confirmed not to exist here.
 *
 * This endpoint only accepts a sdate/edate/date_type range server-side, so
 * every other filter (Entity, Transporter, Destination Zone, etc.) is
 * applied client-side over the fetched rows, same as before. CPD (Customer
 * Promise Date) is sourced from final_eta_dt; CPD Adherence =
 * delivery_datetime <= final_eta_dt, restricted to rows where both are
 * present/valid (see isCpdAdherentRow) - both fields confirmed present on
 * delivered rows from this source (they're simply unset on rows that haven't
 * been delivered yet, which is also true of pod_status/is_submitted_pod
 * below). A POD-aging-in-days field still doesn't exist anywhere, so that one
 * column still renders an honest "pending backend data" placeholder instead
 * of a fabricated number. PTL rows on this source don't carry a
 * reason_for_delay field the way FTL rows do - they carry the same concept
 * under late_delivery_remarks instead - so that value is backfilled onto
 * reason_for_delay at load time (see loadData) rather than duplicating every
 * "Performance Category" grouping/column below for a second field name.
 *
 * Wide date ranges (a multi-month or "since Jan 1" pick) mean this endpoint
 * can be returning tens of thousands of rows, so buildPayload() also opts
 * into two payload-shrinking params the backend added specifically for this
 * (both no-ops for any other/future caller that omits them): `includeLegs:
 * false` skips querying the 2 "legs" collections this dashboard never reads
 * from the response, and `fields: PERFORMANCE_DASHBOARD_FIELDS` projects
 * every query down to the ~28 fields actually used below instead of full
 * 80+-field documents - see filterCombinedConsignmentsOptimized's own doc
 * comment in consignmentsController.js for what these do server-side.
 */

const SEQUENTIAL_BLUE = ['#b7d3f6', '#6da7ec', '#2a78d6', '#1c5cab', '#184f95', '#0d366b'];

const BREACH_BUCKET_ORDER = ['On Time', '0-1 Days', '1-2 Days', '3-5 Days', '6-10 Days', 'Above 10 Days', '(Blank)'];

const DASHBOARD_TAB_META = {
  performance: { title: 'Performance Dashboard', subtitle: 'On-time delivery, breach aging, transporter performance and shipment-level analytics.' },
  transporter: { title: 'Transporter Dashboard', subtitle: 'LR, invoice and weight-wise transporter performance and business share.' },
  ftl: { title: 'FTL Movement', subtitle: 'Fleet utilization, route-wise trips and vehicle capacity analytics for full-truckload movements.' },
  inbound: { title: 'Inbound Dashboard', subtitle: 'Source-wise trips, weight/CBM and expected arrival tracking for inbound shipments.' },
  pod: { title: 'Proof of Delivery', subtitle: 'POD received vs pending, aging buckets and transporter-wise tracking.' },
  co2: { title: 'CO2 Emission', subtitle: 'Carbon footprint, emission intensity and baseline reduction tracking.' },
};

const DATE_TYPE_OPTIONS = [
  { value: 'Dispatch', label: 'Dispatch Date' },
  { value: 'Invoice', label: 'Invoice Date' },
];

const TRACKING_MODE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'FTL', label: 'FTL' },
  { value: 'PTL', label: 'PTL' },
];

// Every source field this dashboard reads anywhere - computeDerived(), the
// filter-option builders in loadData(), and buildShipmentDumpColumnDefs() -
// sent to filterCombinedConsignmentsOptimized's opt-in `fields` projection so
// it never fetches/sorts/ships the ~50+ other fields each source document
// carries. `last_process_on` doesn't need to be listed - the backend always
// includes it since it's required for the cross-collection merge sort.
const PERFORMANCE_DASHBOARD_FIELDS = [
  'lr_no', 'invoice_no', 'invoice_type', 'invoice_time', 'dispatch_date',
  'delivery_datetime', 'expected_eta', 'final_eta_dt', 'transit_time',
  'consigner_zone', 'consigner_city', 'consignee_zone', 'consignee_state', 'consignee_city',
  'shipment_mode', 'tracking_type', 'transporter_name', 'tpt_category', 'truck_type',
  'truck_seal_entity', 'status', 'consignment_status', 'ontime_status',
  'transit_delay', 'transit_delay_hours', 'reason_for_delay', 'late_delivery_remarks',
  'pod_status',
];

const DEST_DIMENSIONS = [
  { key: 'consignee_zone', label: 'Zone' },
  { key: 'consignee_state', label: 'State' },
  { key: 'consignee_city', label: 'City' },
];

// Matches consolidatedScreen.js's own "Performance" grid column valueGetter
// exactly (field: ontime_status, colId: ontime_status) - confirmed this is
// what the user filters via the grid's own column-filter icon, so this must
// mirror that valueGetter, not the separate server-side data_type filter:
//   sla_considered==='yes' -> Ontime
//   else ontime_status===1 && transit_delay===0 -> Ontime
//   else ontime_status===0 || transit_delay===1 -> Delay
//   else -> blank (neither)
// sla_considered doesn't exist anywhere in filterCombinedConsignmentsOptimized's
// real sample data (FTL or PTL), so that branch is omitted rather than
// referencing a field confirmed not to be present here - it would never fire
// regardless.
function isOntimeRow(row) {
  return Number(row.ontime_status) === 1 && Number(row.transit_delay) === 0;
}

function isDelayRow(row) {
  return Number(row.ontime_status) === 0 || Number(row.transit_delay) === 1;
}

// Three-way label for the Shipment Level Dump's "Performance" column,
// matching the live All tab exactly: blank stays blank, never forced into
// "Delay" just because a row isn't "Ontime".
function performanceLabelOf(row) {
  if (!isDeliveredRow(row)) return '';
  if (isOntimeRow(row)) return 'Ontime';
  if (isDelayRow(row)) return 'Delay';
  return '';
}

// Matches consolidatedScreen.js's all_delivered bucket exactly (fragmentData,
// the "Main Projects" All tab's own Delivered condition) - narrower than the
// shared isDeliveredRow() helper used by Transporter/POD dashboards, which
// also allows "trip completed"; kept local to this file on purpose.
function isDeliveredRow(row) {
  if (!row) return false;
  if (row.consignment_status === 'Delivered') return true;
  // The numeric status codes only mean anything for FTL - that field comes
  // from consignment_truck_level (the FTL collection); PTL rows come from
  // courier_cons_lr_no_wise, which confirmed has no numeric `status` field at
  // all on real sample data, so the All tab's own OR-by-status-code branch is
  // naturally a no-op for PTL there too. Kept as an explicit FTL guard rather
  // than relying on that field being absent, in case a future PTL record
  // ever does carry a stray `status` value.
  return row.tracking_type === 'FTL' && [5, 8].includes(Number(row.status));
}

/** Matches the PDF's original "Pending Aging" SWITCH exactly (Status=Delivered
 *  && Performance=Ontime && delay_days=0 -> On Time; Performance=Delay &&
 *  delay_days=0 -> 0-1 Days; then 1-2/3-5/6-10/>10 Days by delay_days; blank
 *  delay_days -> (Blank)). filterCombinedConsignmentsOptimized has no
 *  transit_delay_days field (that was specific to the old ML-service source) -
 *  it only has transit_delay_hours, so row.transit_delay_days is backfilled as
 *  transit_delay_hours / 24 at load time (see loadData) rather than
 *  duplicating this whole bucketing switch for an hours-based variant. */
function getBreachBucket(row) {
  if (!isDeliveredRow(row)) return null;
  const ontime = isOntimeRow(row);
  const raw = row.transit_delay_days;
  const hasDays = raw !== undefined && raw !== null && raw !== '';
  const days = hasDays ? Number(raw) : null;
  if (ontime && (days === null || days === 0)) return 'On Time';
  if (!hasDays || isNaN(days)) return '(Blank)';
  if (!ontime && days === 0) return '0-1 Days';
  if (days > 0 && days <= 2) return '1-2 Days';
  if (days > 2 && days <= 5) return '3-5 Days';
  if (days > 5 && days <= 10) return '6-10 Days';
  if (days > 10) return 'Above 10 Days';
  return '(Blank)';
}

// Matches consolidatedScreen.js's own statusMap exactly (numeric truck-level
// `status` -> label) - FTL rows on filterCombinedConsignmentsOptimized never
// carry a consignment_status string (that field only exists on the PTL/
// courier collections), so the Shipment Level Dump's "Status" column needs
// this fallback the same way the All tab's own Status column does.
const STATUS_MAP = {
  0: 'At Source',
  1: 'Dispatched from Source',
  2: 'In Transit',
  3: 'Arrived At Destination',
  4: 'Out For Delivery',
  5: 'Delivered',
  6: 'Force Closure',
  7: 'Provision',
  8: 'Delivered',
  9: 'Cancelled',
  10: 'Trip Created',
  11: 'Undelivered',
};

function statusLabelOf(row) {
  if (!row) return '';
  return row.consignment_status || STATUS_MAP[Number(row.status)] || '';
}

function dispatchDateOf(row, dateType) {
  const value = dateType === 'Invoice' ? row.invoice_time : (row.dispatch_date || row.invoice_time);
  return value ? moment(value) : null;
}

function deliveryDateOf(row) {
  return row.delivery_datetime ? moment(row.delivery_datetime) : null;
}

function cpdDateOf(row) {
  return row.final_eta_dt ? moment(row.final_eta_dt) : null;
}

/** true/false = adherent or not; null = can't be determined (missing CPD or
 *  missing/invalid delivery date on this row) - null rows are excluded from
 *  CPD Adherence % rather than counted as non-adherent. */
function isCpdAdherentRow(row) {
  const cpd = cpdDateOf(row);
  const deliveredAt = deliveryDateOf(row);
  if (!cpd || !cpd.isValid() || !deliveredAt || !deliveredAt.isValid()) return null;
  return deliveredAt.isSameOrBefore(cpd);
}

function monthKeyOf(m) {
  return m && m.isValid() ? m.format('MMM YYYY') : null;
}

function monthRange(start, end) {
  const months = [];
  let cursor = moment(start).startOf('month');
  const last = moment(end).startOf('month');
  while (cursor.isSameOrBefore(last)) {
    months.push(cursor.format('MMM YYYY'));
    cursor = cursor.add(1, 'month');
  }
  return months;
}

/** For "share of shipments delayed by category" style metrics, where LOW is
 *  good and HIGH is bad - the inverse of every on-time/adherence % elsewhere
 *  on this dashboard. Mirrors the 85 threshold around 100 (<=15 good). */
function inverseStatusClassForPercent(pct) {
  if (pct === null || pct === undefined || isNaN(pct)) return '';
  return pct <= 15 ? 'pdm-status-good' : 'pdm-status-warning';
}

function inversePercentCellClass(params) {
  return inverseStatusClassForPercent(params.value);
}

function buildTransporterMonthGrid(d) {
  const { transporterMonthTable } = d;
  const records = transporterMonthTable.transporters.map((t) => {
    const row = { transporter: t };
    transporterMonthTable.months.forEach((m) => { row[m] = transporterMonthTable.cell(t, m); });
    return row;
  });
  const columnDefs = [
    { headerName: 'Transporter', field: 'transporter', pinned: 'left', width: 200 },
    ...transporterMonthTable.months.map((m) => ({
      headerName: m, field: m, width: 120, valueFormatter: percentValueFormatter, cellClass: percentCellClass,
    })),
  ];
  const pinnedBottomRowData = [{
    transporter: 'Average',
    ...Object.fromEntries(transporterMonthTable.months.map((m) => {
      const vals = transporterMonthTable.transporters.map((t) => transporterMonthTable.cell(t, m)).filter((v) => v !== null);
      return [m, vals.length ? round1(average(vals)) : null];
    })),
  }];
  return { columnDefs, records, pinnedBottomRowData };
}

function buildAvgDaysGrid(d) {
  const { avgDaysPivot } = d;
  const records = avgDaysPivot.transporters.map((t) => {
    const row = { transporter: t };
    avgDaysPivot.states.forEach((s) => {
      const items = avgDaysPivot.cell(t, s);
      row[s] = items.length ? round1(average(items.map((r) => r.__days))) : null;
    });
    return row;
  });
  const columnDefs = [
    { headerName: 'Transporter', field: 'transporter', pinned: 'left', width: 200 },
    ...avgDaysPivot.states.map((s) => ({ headerName: s, field: s, width: 130 })),
  ];
  const pinnedBottomRowData = [{ transporter: 'State Average', ...avgDaysPivot.totals }];
  return { columnDefs, records, pinnedBottomRowData };
}

function buildLrDeliveredGrid(d) {
  const { lrDeliveredTable } = d;
  const records = lrDeliveredTable.transporters.map((t) => {
    const row = { transporter: t };
    lrDeliveredTable.months.forEach((m) => { row[m] = lrDeliveredTable.cell(t, m).length; });
    row.total = lrDeliveredTable.rowTotals[t];
    return row;
  });
  const columnDefs = [
    { headerName: 'Transporter', field: 'transporter', pinned: 'left', width: 200 },
    ...lrDeliveredTable.months.map((m) => ({ headerName: m, field: m, width: 110 })),
    { headerName: 'Total', field: 'total', width: 110, pinned: 'right', cellClass: 'pdm-ag-strong' },
  ];
  const pinnedBottomRowData = [{ transporter: 'Grand Total', ...lrDeliveredTable.colTotals, total: lrDeliveredTable.grandTotal }];
  return { columnDefs, records, pinnedBottomRowData };
}

function buildPerformanceCategoryGrid(d) {
  const records = d.performanceCategoryRows.map((r) => ({ category: r.category, delay: r.delay, onTime: r.onTime, blank: r.blank, total: r.total }));
  const columnDefs = [
    { headerName: 'Performance Category', field: 'category', pinned: 'left', width: 220 },
    { headerName: 'Delay', field: 'delay', width: 110 },
    { headerName: 'On-Time', field: 'onTime', width: 110 },
    { headerName: 'Blank', field: 'blank', width: 110 },
    { headerName: 'Total', field: 'total', width: 110, cellClass: 'pdm-ag-strong' },
  ];
  const pinnedBottomRowData = [{ category: 'Grand Total', ...d.performanceCategoryTotal }];
  return { columnDefs, records, pinnedBottomRowData };
}

function buildTransporterCategoryGrid(d) {
  const { transporterCategoryPivot } = d;
  const records = transporterCategoryPivot.transporters.map((t) => {
    const row = { transporter: t };
    transporterCategoryPivot.categories.forEach((c) => { row[c] = transporterCategoryPivot.pct(t, c); });
    row.totalDelayPct = transporterCategoryPivot.rowTotal(t);
    return row;
  });
  const columnDefs = [
    { headerName: 'Transporter', field: 'transporter', pinned: 'left', width: 200 },
    ...transporterCategoryPivot.categories.map((c) => ({ headerName: c, field: c, width: 150, valueFormatter: percentValueFormatter, cellClass: inversePercentCellClass })),
    {
      headerName: 'Total Delay %',
      field: 'totalDelayPct',
      width: 140,
      pinned: 'right',
      valueFormatter: percentValueFormatter,
      cellClass: (params) => ['pdm-ag-strong', inverseStatusClassForPercent(params.value)],
    },
  ];
  const pinnedBottomRowData = [{
    transporter: 'Grand Total',
    ...Object.fromEntries(transporterCategoryPivot.categories.map((c) => [c, transporterCategoryPivot.grandTotal(c)])),
    totalDelayPct: null,
  }];
  return { columnDefs, records, pinnedBottomRowData };
}

function buildRouteAdherenceGrid(d) {
  const records = d.routeAdherenceRows.map((r) => ({ route: r.route, transporter: r.transporter, lrCount: r.lrCount, cpdAdherence: r.cpdAdherence, etaAdherence: r.etaAdherence }));
  const columnDefs = [
    { headerName: 'Route', field: 'route', pinned: 'left', width: 220 },
    { headerName: 'Transporter', field: 'transporter', width: 200 },
    { headerName: '# LR', field: 'lrCount', width: 100 },
    { headerName: 'CPD Adherence', field: 'cpdAdherence', width: 150, valueFormatter: percentValueFormatter, cellClass: percentCellClass },
    { headerName: 'ETA Adherence', field: 'etaAdherence', width: 150, valueFormatter: percentValueFormatter, cellClass: percentCellClass },
  ];
  const pinnedBottomRowData = [{ route: 'Grand Total', transporter: '', lrCount: d.routeAdherenceTotal.lrCount, cpdAdherence: d.routeAdherenceTotal.cpdAdherence, etaAdherence: d.routeAdherenceTotal.etaAdherence }];
  return { columnDefs, records, pinnedBottomRowData };
}

function buildCpdEtaTransporterGrid(d) {
  const { transporterMonthTable, cpdMonthTable } = d;
  const records = transporterMonthTable.transporters.map((t) => {
    const row = { transporter: t };
    transporterMonthTable.months.forEach((m) => {
      row[`${m}__cpd`] = cpdMonthTable.cell(t, m);
      row[`${m}__eta`] = transporterMonthTable.cell(t, m);
    });
    return row;
  });
  const columnDefs = [
    { headerName: 'Transporter', field: 'transporter', pinned: 'left', width: 200 },
    ...transporterMonthTable.months.map((m) => ({
      headerName: m,
      children: [
        { headerName: 'CPD %', field: `${m}__cpd`, width: 100, valueFormatter: percentValueFormatter, cellClass: percentCellClass },
        { headerName: 'ETA %', field: `${m}__eta`, width: 100, valueFormatter: percentValueFormatter, cellClass: percentCellClass },
      ],
    })),
  ];
  return { columnDefs, records, pinnedBottomRowData: null };
}

function buildShipmentDumpColumnDefs() {
  return [
    { headerName: 'LR No', field: 'lr_no', width: 130, pinned: 'left' },
    { headerName: 'Invoice No', field: 'invoice_no', width: 130 },
    { headerName: 'Invoice Type', field: 'invoice_type', width: 150 },
    { headerName: 'Dispatch Date', field: 'dispatch_date', width: 150, valueGetter: (p) => (p.data.dispatch_date ? getHyphenDDMMYYYY(p.data.dispatch_date) : '') },
    { headerName: 'Source Zone', field: 'consigner_zone', width: 130 },
    { headerName: 'Source City', field: 'consigner_city', width: 130 },
    { headerName: 'Destination Zone', field: 'consignee_zone', width: 140 },
    { headerName: 'Destination State', field: 'consignee_state', width: 150 },
    { headerName: 'Destination City', field: 'consignee_city', width: 140 },
    { headerName: 'Mode', field: 'shipment_mode', width: 110 },
    { headerName: 'Tracking Mode', field: 'tracking_type', width: 130 },
    { headerName: 'Movement Type', field: 'movement_type_derived', width: 140 },
    { headerName: 'Transporter', field: 'transporter_name', width: 180 },
    { headerName: 'Transporter Type', field: 'tpt_category', width: 150 },
    { headerName: 'Vehicle Type', field: 'truck_type', width: 130 },
    { headerName: 'Actual Delivery Date', field: 'delivery_datetime', width: 170, valueGetter: (p) => (p.data.delivery_datetime ? getHyphenDDMMMYYYYHHMM(p.data.delivery_datetime) : '') },
    { headerName: 'Expected ETA', field: 'expected_eta', width: 160, valueGetter: (p) => (p.data.expected_eta ? getHyphenDDMMMYYYYHHMM(p.data.expected_eta) : '') },
    { headerName: 'TAT', field: 'transit_time', width: 100 },
    { headerName: 'Status', width: 160, valueGetter: (p) => statusLabelOf(p.data) },
    {
      headerName: 'Performance',
      width: 120,
      valueGetter: (p) => performanceLabelOf(p.data),
    },
    { headerName: 'Delay Bucket', width: 130, valueGetter: (p) => getBreachBucket(p.data) || '' },
    {
      headerName: 'POD Status',
      width: 130,
      valueGetter: (p) => p.data.pod_status || (isDeliveredRow(p.data) ? 'Pending' : ''),
    },
    {
      headerName: 'POD Aging',
      width: 110,
      headerTooltip: 'No aging/duration field exists for POD yet - only submitted/pending is available.',
      valueGetter: () => '—',
    },
    {
      headerName: 'CPD',
      width: 160,
      headerTooltip: 'Customer Promise Date (final_eta_dt).',
      valueGetter: (p) => (p.data.final_eta_dt ? getHyphenDDMMMYYYYHHMM(p.data.final_eta_dt) : ''),
    },
    { headerName: 'Late Delivery Remarks', field: 'reason_for_delay', width: 200 },
  ];
}

export default class PerformanceDashboardModern extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeDashboardTab: 'performance',
      loading: true,
      error: null,
      lastRefreshed: null,

      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        trackingMode: TRACKING_MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        lateDeliveryRemarks: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },

      filterOptions: {
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
        lateDeliveryRemarks: [],
      },

      destGroupBy: 'consignee_zone',

      rawRows: [],
      filteredRows: [],
      derived: null,
    };

    this.shipmentDumpColumnDefs = buildShipmentDumpColumnDefs();
  }

  componentDidMount() {
    this.loadLateDeliveryRemarkOptions();
    this.loadData();
  }

  loadLateDeliveryRemarkOptions() {
    redirectURL.post('/consignments/reasonForDelayData', {})
      .then((resp) => {
        const remarks = (resp.data && resp.data[0] && resp.data[0].master_data) || [];
        this.setState((prev) => ({
          filterOptions: {
            ...prev.filterOptions,
            lateDeliveryRemarks: remarks.map((r) => ({ value: r, label: r })),
          },
        }));
      })
      .catch(() => {});
  }

  buildPayload() {
    const f = this.state.filters;
    return {
      // filterCombinedConsignmentsOptimized's own params (identical contract
      // to filterCombinedConsignments - see consignmentsController.js):
      // sdate/edate (plain YYYY-MM-DD, parsed server-side as a UTC day range)
      // and date_type, which takes the literal "Dispatch"/"Invoice" tokens -
      // DATE_TYPE_OPTIONS' values already are those tokens, so no conversion
      // is needed here (unlike the old getDataForPowerBiDashBoard contract,
      // which took short "invoice"/"dispatch" tokens instead).
      sdate: moment(f.startDate).format('YYYY-MM-DD'),
      edate: moment(f.endDate).format('YYYY-MM-DD'),
      date_type: f.dateType.value,
      // "all": no server-side status exclusion - matches the All tab, which
      // applies its own eligibility/status rules client-side (mirrored below
      // in computeDerived's `rows` filter) rather than at the query level.
      tabType: 'all',
      // "Total": return every row in range, before any of this endpoint's
      // bucket-style post-filters (Delivered/ontime/Delayed/etc.) - those
      // buckets are recomputed client-side here anyway, from the full set.
      data_type: 'Total',
      // This dashboard only ever reads resp.data.totalConsignments, never
      // resp.data.consignments (the "legs" collections) - skip querying/
      // sorting/serializing those entirely rather than paying for 2 extra
      // full collection scans of data that gets thrown away on arrival.
      includeLegs: false,
      // Field projection - each source document carries 80+ fields; this is
      // every one this dashboard actually reads anywhere (computeDerived,
      // the filter-option builders, and buildShipmentDumpColumnDefs), so
      // Mongo/Node never fetch, sort or ship the other ~50+ over the wire.
      // Keep this list in sync with PERFORMANCE_DASHBOARD_FIELDS' usages
      // below if a future widget needs a field not already listed here.
      fields: PERFORMANCE_DASHBOARD_FIELDS,
    };
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    redirectURL.post('consignments/filterCombinedConsignmentsOptimized', this.buildPayload())
      .then((resp) => {
        const rows = safeArray(resp.data && resp.data.totalConsignments);
        rows.forEach((row) => {
          // The source movement_type field is mostly blank on this feed - derive
          // Primary/Secondary the same way the original Power BI measure does
          // (Primary/Secondary = IF(invoice_type = "stock transfer", "Primary", "Secondary")).
          row.movement_type_derived = /stock transfer/i.test(row.invoice_type || '') ? 'Primary' : 'Secondary';
          // PTL rows on this source carry their delay reason under
          // late_delivery_remarks instead of reason_for_delay (which only
          // FTL rows have) - backfill it onto reason_for_delay so every
          // "Performance Category"/"Late Delivery Remarks" grouping and
          // column below works for both tracking types without a second
          // field name threaded through.
          if (!row.reason_for_delay && row.late_delivery_remarks) {
            row.reason_for_delay = row.late_delivery_remarks;
          }
          // This source has no transit_delay_days field (that was specific
          // to the old ML-service source) - only transit_delay_hours -
          // so derive it for getBreachBucket() rather than losing the
          // breach-aging widgets entirely.
          if (row.transit_delay_days === undefined && row.transit_delay_hours !== undefined && row.transit_delay_hours !== null) {
            row.transit_delay_days = Number(row.transit_delay_hours) / 24;
          }
        });

        const filterOptions = {
          entity: distinctValues(rows, 'truck_seal_entity').map((v) => ({ value: v, label: v })),
          movementType: distinctValues(rows, 'movement_type_derived').map((v) => ({ value: v, label: v })),
          sourceZone: distinctValues(rows, 'consigner_zone').map((v) => ({ value: v, label: v })),
          sourceCity: distinctValues(rows, 'consigner_city').map((v) => ({ value: v, label: v })),
          destinationZone: distinctValues(rows, 'consignee_zone').map((v) => ({ value: v, label: v })),
          transporterCategory: distinctValues(rows, 'tpt_category').map((v) => ({ value: v, label: v })),
          invoiceType: distinctValues(rows, 'invoice_type').map((v) => ({ value: v, label: v })),
          transporter: distinctValues(rows, 'transporter_name').map((v) => ({ value: v, label: v })),
        };

        this.setState((prev) => ({
          loading: false,
          lastRefreshed: moment(),
          rawRows: rows,
          filterOptions: { ...prev.filterOptions, ...filterOptions },
        }));
        this.applyClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching performance dashboard data:', error);
        this.setState({ loading: false, error: 'Unable to load performance dashboard data. Please try again.' });
      });
  };

  applyClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.rawRows;
    const f = this.state.filters;
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      const values = selection.map((o) => o.value);
      return values.includes(row[field]);
    };
    const filtered = rows.filter((row) => (
      matches(row, f.entity, 'truck_seal_entity')
      && matches(row, f.movementType, 'movement_type_derived')
      && matches(row, f.sourceZone, 'consigner_zone')
      && matches(row, f.sourceCity, 'consigner_city')
      && matches(row, f.destinationZone, 'consignee_zone')
      && matches(row, f.transporterCategory, 'tpt_category')
      && matches(row, f.invoiceType, 'invoice_type')
      && matches(row, f.lateDeliveryRemarks, 'reason_for_delay')
      && matches(row, f.transporter, 'transporter_name')
      && (f.trackingMode.value === 'All' || row.tracking_type === f.trackingMode.value)
    ));
    this.setState({ filteredRows: filtered, derived: this.computeDerived(filtered) });
  };

  computeDerived(rawRows) {
    const { filters } = this.state;
    // Matches consolidatedScreen.js's all_tripsar eligibility filter exactly -
    // applied to EVERY row before anything is counted for the All tab's
    // "all_trips": must have a tracking_type at all, FTL rows exclude status
    // 6/9, PTL rows exclude consignment_status "Cancelled", and lr_no must
    // be present-and-non-empty. Deliberately checking `!== '' && !== undefined`
    // rather than a plain falsy check - the source code's own
    // `lr_no !== "" && lr_no !== undefined` guard means a row with lr_no
    // explicitly set to null (key present, value null) still PASSES there,
    // so a naive `!row.lr_no` here would wrongly exclude it. A row with some
    // other/missing tracking_type is excluded entirely (matches that code -
    // only the FTL and PTL branches admit a row). Applied here, not in
    // applyClientFilters, so it always matches the All tab regardless of
    // which other filters are active.
    const rows = rawRows.filter((row) => {
      if (!row || !row.tracking_type) return false;
      if (row.lr_no === '' || row.lr_no === undefined) return false;
      if (row.tracking_type === 'FTL') return row.status !== 6 && row.status !== 9;
      if (row.tracking_type === 'PTL') return row.consignment_status !== 'Cancelled';
      return false;
    });
    // No LR+dispatch-date dedup here on purpose - consolidatedScreen.js's All
    // tab counts all_delivered.length as a plain filtered-array length with no
    // dedup, so every widget built from "delivered" mirrors that directly.
    const delivered = rows.filter(isDeliveredRow);
    const ontimeCount = delivered.filter(isOntimeRow).length;
    const ontimePercent = delivered.length ? round1((ontimeCount / delivered.length) * 100) : 0;

    const withDays = delivered
      .map((r) => {
        const dispatch = dispatchDateOf(r, filters.dateType.value);
        const deliveredAt = deliveryDateOf(r);
        if (!dispatch || !deliveredAt || !dispatch.isValid() || !deliveredAt.isValid()) return null;
        const days = deliveredAt.diff(dispatch, 'days', true);
        return days >= 0 ? { ...r, __days: days } : null;
      })
      .filter(Boolean);
    const avgDeliveryDays = round1(average(withDays.map((r) => r.__days)));

    // KPI2: month-wise on-time trend
    const monthGroups = groupBy(delivered, (r) => monthKeyOf(dispatchDateOf(r, filters.dateType.value)));
    const months = monthRange(filters.startDate, filters.endDate);
    const monthlyTrend = {
      categories: months,
      data: months.map((m) => {
        const items = monthGroups.get(m) || [];
        return items.length ? round1((items.filter(isOntimeRow).length / items.length) * 100) : null;
      }),
    };

    // CPD monthly trend (delivered_datetime <= final_eta_dt), only over rows
    // where CPD adherence can actually be determined (both dates present/valid).
    const cpdMonthGroups = groupBy(
      delivered.filter((r) => isCpdAdherentRow(r) !== null),
      (r) => monthKeyOf(dispatchDateOf(r, filters.dateType.value)),
    );
    const cpdMonthlyTrend = {
      categories: months,
      data: months.map((m) => {
        const items = cpdMonthGroups.get(m) || [];
        return items.length ? round1((items.filter((r) => isCpdAdherentRow(r) === true).length / items.length) * 100) : null;
      }),
    };

    // KPI3: Origin City
    const cityGroups = groupBy(delivered, (r) => r.consigner_city || 'Unspecified');
    const originCity = Array.from(cityGroups.entries())
      .map(([name, items]) => ({ name, pct: round1((items.filter(isOntimeRow).length / items.length) * 100), count: items.length }))
      .sort((a, b) => b.count - a.count);

    // KPI4: destination (zone/state/city, toggled)
    const buildDestination = (field) => {
      const groups = groupBy(delivered, (r) => r[field] || 'Unspecified');
      return Array.from(groups.entries())
        .map(([name, items]) => ({ name, pct: round1((items.filter(isOntimeRow).length / items.length) * 100), count: items.length }))
        .sort((a, b) => b.pct - a.pct);
    };
    const destinationByDimension = {
      consignee_zone: buildDestination('consignee_zone'),
      consignee_state: buildDestination('consignee_state'),
      consignee_city: buildDestination('consignee_city'),
    };

    // KPI5: transporter
    const transporterGroups = groupBy(delivered, (r) => r.transporter_name || 'Unspecified');
    const transporterChart = Array.from(transporterGroups.entries())
      .map(([name, items]) => ({ name, pct: round1((items.filter(isOntimeRow).length / items.length) * 100), count: items.length }))
      .sort((a, b) => b.pct - a.pct);

    // KPI6: breach bucket distribution
    const bucketGroups = groupBy(delivered, getBreachBucket);
    const breachBuckets = BREACH_BUCKET_ORDER
      .filter((name) => name !== '(Blank)' || (bucketGroups.get('(Blank)') || []).length > 0)
      .map((name) => {
        const items = bucketGroups.get(name) || [];
        return { name, count: items.length, pct: delivered.length ? round1((items.length / delivered.length) * 100) : 0 };
      });

    // KPI7: transporter x month on-time %
    const transporters = Array.from(new Set(delivered.map((r) => r.transporter_name).filter(Boolean)))
      .sort((a, b) => (transporterGroups.get(b) || []).length - (transporterGroups.get(a) || []).length);
    const monthPivot = buildPivot(
      delivered,
      (r) => r.transporter_name,
      months,
      (r) => monthKeyOf(dispatchDateOf(r, filters.dateType.value)),
    );
    const transporterMonthTable = {
      transporters,
      months,
      cell: (t, m) => {
        const items = monthPivot.cell(t, m);
        return items.length ? round1((items.filter(isOntimeRow).length / items.length) * 100) : null;
      },
    };

    // Same transporter x month pivot, but for CPD adherence (used by the
    // "CPD vs ETA (Transporter Wise)" grid's CPD % sub-columns).
    const cpdDeliveredForPivot = delivered.filter((r) => isCpdAdherentRow(r) !== null);
    const cpdMonthPivot = buildPivot(
      cpdDeliveredForPivot,
      (r) => r.transporter_name,
      months,
      (r) => monthKeyOf(dispatchDateOf(r, filters.dateType.value)),
    );
    const cpdMonthTable = {
      transporters,
      months,
      cell: (t, m) => {
        const items = cpdMonthPivot.cell(t, m);
        return items.length ? round1((items.filter((r) => isCpdAdherentRow(r) === true).length / items.length) * 100) : null;
      },
    };

    // KPI8: avg delivery days pivot (transporter x state)
    const states = Array.from(new Set(withDays.map((r) => r.consignee_state).filter(Boolean))).sort();
    const hasBlankState = withDays.some((r) => !r.consignee_state);
    const stateColumns = hasBlankState ? [...states, 'Blank'] : states;
    const avgDaysPivot = buildPivot(
      withDays,
      (r) => r.transporter_name || 'Unspecified',
      stateColumns,
      (r) => r.consignee_state || 'Blank',
    );
    const avgDaysTransporters = Array.from(new Set(withDays.map((r) => r.transporter_name || 'Unspecified')));
    const avgDaysTotals = {};
    stateColumns.forEach((state) => {
      const items = withDays.filter((r) => (r.consignee_state || 'Blank') === state);
      avgDaysTotals[state] = items.length ? round1(average(items.map((r) => r.__days))) : null;
    });

    // KPI9: transporter-wise LR's delivered (month x transporter, count)
    const lrPivot = buildPivot(
      delivered,
      (r) => r.transporter_name || 'Unspecified',
      months,
      (r) => monthKeyOf(dispatchDateOf(r, filters.dateType.value)),
    );
    const lrTransporters = Array.from(new Set(delivered.map((r) => r.transporter_name || 'Unspecified')))
      .sort((a, b) => (transporterGroups.get(b) || []).length - (transporterGroups.get(a) || []).length);
    const lrRowTotals = {};
    lrTransporters.forEach((t) => {
      lrRowTotals[t] = sum(months.map((m) => lrPivot.cell(t, m).length));
    });
    const lrColTotals = {};
    months.forEach((m) => {
      lrColTotals[m] = sum(lrTransporters.map((t) => lrPivot.cell(t, m).length));
    });
    const lrGrandTotal = sum(Object.values(lrRowTotals));

    // KPI10: performance category summary (Performance Category = reason_for_delay,
    // backfilled from late_delivery_remarks for PTL rows at load time - see
    // loadData - since only FTL rows carry reason_for_delay natively on
    // filterCombinedConsignmentsOptimized)
    const remarkGroups = groupBy(delivered, (r) => r.reason_for_delay || 'Not Specified');
    const performanceCategoryRows = Array.from(remarkGroups.entries()).map(([category, items]) => {
      const delay = items.filter(isDelayRow).length;
      const onTime = items.filter(isOntimeRow).length;
      const blank = items.length - delay - onTime;
      return { category, delay, onTime, blank, total: items.length };
    }).sort((a, b) => b.total - a.total);
    const performanceCategoryTotal = performanceCategoryRows.reduce((acc, r) => ({
      delay: acc.delay + r.delay, onTime: acc.onTime + r.onTime, blank: acc.blank + r.blank, total: acc.total + r.total,
    }), { delay: 0, onTime: 0, blank: 0, total: 0 });

    // KPI11: transporter-wise performance category (%)
    const categories = Array.from(remarkGroups.keys());
    const catPivot = buildPivot(
      delivered,
      (r) => r.transporter_name || 'Unspecified',
      categories,
      (r) => r.reason_for_delay || 'Not Specified',
    );
    const catTransporterTotals = {};
    transporters.forEach((t) => { catTransporterTotals[t] = (transporterGroups.get(t) || []).length; });
    const transporterCategoryPivot = {
      transporters,
      categories,
      pct: (t, c) => {
        const denom = catTransporterTotals[t] || 0;
        if (!denom) return null;
        const items = catPivot.cell(t, c).filter(isDelayRow);
        return round1((items.length / denom) * 100);
      },
      rowTotal: (t) => {
        const denom = catTransporterTotals[t] || 0;
        if (!denom) return 0;
        const delayCount = delivered.filter((r) => r.transporter_name === t && isDelayRow(r)).length;
        return round1((delayCount / denom) * 100);
      },
      grandTotal: (c) => {
        const items = delivered.filter((r) => (r.reason_for_delay || 'Not Specified') === c && isDelayRow(r));
        return delivered.length ? round1((items.length / delivered.length) * 100) : 0;
      },
    };

    // Route-wise CPD vs ETA Adherence. ETA Adherence reuses the same real
    // on-time/expected-ETA measure as the rest of the dashboard; CPD Adherence
    // is delivery_datetime <= final_eta_dt, restricted to rows where both
    // dates are present/valid (see isCpdAdherentRow).
    const routeGroups = groupBy(delivered, (r) => `${r.consigner_city || 'Unknown'} - ${r.consignee_city || 'Unknown'}||${r.transporter_name || 'Unspecified'}`);
    const routeAdherenceRows = Array.from(routeGroups.entries())
      .map(([key, items]) => {
        const [route, transporter] = key.split('||');
        const cpdItems = items.filter((r) => isCpdAdherentRow(r) !== null);
        return {
          route,
          transporter,
          lrCount: items.length,
          etaAdherence: round1((items.filter(isOntimeRow).length / items.length) * 100),
          cpdAdherence: cpdItems.length ? round1((cpdItems.filter((r) => isCpdAdherentRow(r) === true).length / cpdItems.length) * 100) : null,
        };
      })
      .sort((a, b) => b.lrCount - a.lrCount);
    const cpdDeliveredItems = delivered.filter((r) => isCpdAdherentRow(r) !== null);
    const routeAdherenceTotal = {
      lrCount: sum(routeAdherenceRows.map((r) => r.lrCount)),
      etaAdherence: delivered.length ? round1((delivered.filter(isOntimeRow).length / delivered.length) * 100) : 0,
      cpdAdherence: cpdDeliveredItems.length ? round1((cpdDeliveredItems.filter((r) => isCpdAdherentRow(r) === true).length / cpdDeliveredItems.length) * 100) : null,
    };

    return {
      ontimePercent,
      ontimeCount,
      delayCount: delivered.filter(isDelayRow).length,
      deliveredTotal: delivered.length,
      avgDeliveryDays,
      totalLRs: rows.length,
      monthlyTrend,
      cpdMonthlyTrend,
      originCity,
      destinationByDimension,
      transporterChart,
      breachBuckets,
      transporterMonthTable,
      cpdMonthTable,
      avgDaysPivot: { transporters: avgDaysTransporters, states: stateColumns, cell: avgDaysPivot.cell, totals: avgDaysTotals },
      lrDeliveredTable: { transporters: lrTransporters, months, cell: lrPivot.cell, rowTotals: lrRowTotals, colTotals: lrColTotals, grandTotal: lrGrandTotal },
      performanceCategoryRows,
      performanceCategoryTotal,
      transporterCategoryPivot,
      routeAdherenceRows,
      routeAdherenceTotal,
    };
  }

  onFilterChange = (key, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }));
  };

  onApply = () => {
    this.loadData();
  };

  onReset = () => {
    this.setState({
      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        trackingMode: TRACKING_MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        lateDeliveryRemarks: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },
    }, () => this.loadData());
  };

  renderEmptyState(message, hint) {
    return (
      <div className="pdm-empty-state">
        <div className="pdm-empty-state-icon">&#9679;</div>
        <strong>{message}</strong>
        {hint && <span>{hint}</span>}
      </div>
    );
  }

  renderChartCard(title, subtitle, options, height = 320, minWidth = null) {
    return (
      <div className="pdm-card">
        {title && <p className="pdm-card-title">{title}</p>}
        {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
        {options ? (
          <div className={minWidth ? 'pdm-chart-flex pdm-chart-scroll' : 'pdm-chart-flex'} style={{ height }}>
            <div style={{ height: '100%', minWidth: minWidth || undefined }}>
              <HighchartsReact
                highcharts={Highcharts}
                containerProps={{ style: { width: '100%', height: '100%' } }}
                options={{ ...options, chart: { ...options.chart, height } }}
                updateArgs={[true, true, true]}
              />
            </div>
          </div>
        ) : this.renderEmptyState('No data available', 'Try widening the date range or clearing a filter.')}
      </div>
    );
  }

  buildGaugeCard(pct) {
    const color = statusColorForPercent(pct);
    const clamped = Math.max(0, Math.min(pct, 100));
    return (
      <div className="pdm-card">
        <p className="pdm-card-title">On-Time Delivery (%)</p>
        <p className="pdm-card-subtitle">Overall on-time percentage for delivered shipments in range.</p>
        <div className="pdm-gauge-vwrap pdm-chart-flex" style={{ height: 320, justifyContent: 'center' }}>
          <div className={`pdm-gauge-value ${statusClassForPercent(pct)}`}>{pct}%</div>
          <div className="pdm-gauge-vtrack">
            <div className="pdm-gauge-vtarget" style={{ bottom: '85%' }} title="85% target" />
            <div className="pdm-gauge-vfill" style={{ height: `${clamped}%`, background: color }} />
          </div>
          <div className="pdm-gauge-vscale">
            <span>100%</span>
            <span>85% target</span>
            <span>0%</span>
          </div>
        </div>
      </div>
    );
  }

  buildMonthlyTrendOptions(trend) {
    if (!trend.categories.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'spline' },
      title: { text: null },
      xAxis: baseAxisLine(trend.categories),
      yAxis: baseValueAxis('On-Time %', 100),
      legend: { enabled: false },
      plotOptions: { series: { lineWidth: 2, marker: { radius: 4 }, connectNulls: true, color: COLORS.blue } },
      tooltip: { valueSuffix: '%' },
      series: [{ name: 'On-Time %', data: trend.data }],
    };
  }

  buildCpdEtaAdherenceTrendOptions(etaTrend, cpdTrend) {
    if (!etaTrend.categories.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'spline' },
      title: { text: null },
      xAxis: baseAxisLine(etaTrend.categories),
      yAxis: baseValueAxis('Adherence %', 100),
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { series: { lineWidth: 2, marker: { radius: 4 }, connectNulls: true } },
      tooltip: { shared: true, valueSuffix: '%' },
      series: [
        { name: 'CPD Adherence %', data: cpdTrend.data, color: COLORS.violet },
        { name: 'ETA Adherence %', data: etaTrend.data, color: COLORS.blue },
      ],
    };
  }

  buildVerticalPercentBar(items, yTitle) {
    if (!items.length) return null;
    const top = items;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'column' },
      title: { text: null },
      xAxis: { ...baseAxisLine(top.map((i) => i.name)), labels: { ...baseAxisLine([]).labels, rotation: top.length > 8 ? -35 : 0 } },
      yAxis: baseValueAxis(yTitle, 100),
      legend: { enabled: false },
      plotOptions: { series: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, format: '{point.y}%', style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { headerFormat: '', pointFormat: '<b>{point.key}</b><br/>On-time: <b>{point.y}%</b><br/>Shipments: {point.count}' },
      series: [{ name: 'On-Time %', colorByPoint: true, data: top.map((i) => ({ name: i.name, y: i.pct, count: i.count, color: statusColorForPercent(i.pct) })) }],
    };
  }

  buildBreachChartOptions(buckets) {
    if (!buckets.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'column' },
      title: { text: null },
      xAxis: baseAxisLine(buckets.map((b) => b.name)),
      yAxis: baseValueAxis('% of delivered shipments', 100),
      legend: { enabled: false },
      plotOptions: { series: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, format: '{point.y}%', style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { headerFormat: '', pointFormat: '<b>{point.key}</b>: {point.y}% ({point.count} shipments)' },
      series: [{
        name: 'Shipments',
        data: buckets.map((b, idx) => ({ name: b.name, y: b.pct, count: b.count, color: b.name === 'On Time' ? COLORS.good : SEQUENTIAL_BLUE[Math.min(idx, SEQUENTIAL_BLUE.length - 1)] })),
      }],
    };
  }

  render() {
    const { state } = this;
    const f = state.filters;
    const d = state.derived;
    const fo = state.filterOptions;

    return (
      <div className="pdm-root">
        {state.loading && (
          <div className="pdm-loading-overlay"><div className="pdm-spinner" /></div>
        )}

        <div className="pdm-header">
          <div className="pdm-header-titles">
            <h1>{DASHBOARD_TAB_META[state.activeDashboardTab].title}</h1>
            <p>{DASHBOARD_TAB_META[state.activeDashboardTab].subtitle}</p>
          </div>
          <div className="pdm-header-actions">
            {state.activeDashboardTab === 'performance' && state.lastRefreshed && (
              <span className="pdm-refresh-meta">Updated {state.lastRefreshed.format('DD MMM YYYY, HH:mm')}</span>
            )}
            <div className="pdm-dashboard-tabs">
              <button
                type="button"
                data-tooltip={DASHBOARD_TAB_META.performance.title}
                aria-label={DASHBOARD_TAB_META.performance.title}
                className={`pdm-dashboard-tab pdm-tab-performance ${state.activeDashboardTab === 'performance' ? 'pdm-dashboard-tab-active' : ''}`}
                onClick={() => this.setState({ activeDashboardTab: 'performance' })}
              >
                <BarChartIcon />
              </button>
              <button
                type="button"
                data-tooltip={DASHBOARD_TAB_META.transporter.title}
                aria-label={DASHBOARD_TAB_META.transporter.title}
                className={`pdm-dashboard-tab pdm-tab-transporter ${state.activeDashboardTab === 'transporter' ? 'pdm-dashboard-tab-active' : ''}`}
                onClick={() => this.setState({ activeDashboardTab: 'transporter' })}
              >
                <TruckIcon />
              </button>
              <button
                type="button"
                data-tooltip={DASHBOARD_TAB_META.ftl.title}
                aria-label={DASHBOARD_TAB_META.ftl.title}
                className={`pdm-dashboard-tab pdm-tab-ftl ${state.activeDashboardTab === 'ftl' ? 'pdm-dashboard-tab-active' : ''}`}
                onClick={() => this.setState({ activeDashboardTab: 'ftl' })}
              >
                <GaugeIcon />
              </button>
              <button
                type="button"
                data-tooltip={DASHBOARD_TAB_META.inbound.title}
                aria-label={DASHBOARD_TAB_META.inbound.title}
                className={`pdm-dashboard-tab pdm-tab-inbound ${state.activeDashboardTab === 'inbound' ? 'pdm-dashboard-tab-active' : ''}`}
                onClick={() => this.setState({ activeDashboardTab: 'inbound' })}
              >
                <InboundIcon />
              </button>
              <button
                type="button"
                data-tooltip={DASHBOARD_TAB_META.pod.title}
                aria-label={DASHBOARD_TAB_META.pod.title}
                className={`pdm-dashboard-tab pdm-tab-pod ${state.activeDashboardTab === 'pod' ? 'pdm-dashboard-tab-active' : ''}`}
                onClick={() => this.setState({ activeDashboardTab: 'pod' })}
              >
                <PodIcon />
              </button>
              <button
                type="button"
                data-tooltip={DASHBOARD_TAB_META.co2.title}
                aria-label={DASHBOARD_TAB_META.co2.title}
                className={`pdm-dashboard-tab pdm-tab-co2 ${state.activeDashboardTab === 'co2' ? 'pdm-dashboard-tab-active' : ''}`}
                onClick={() => this.setState({ activeDashboardTab: 'co2' })}
              >
                <LeafIcon />
              </button>
            </div>
          </div>
        </div>

        {state.activeDashboardTab === 'transporter' ? (
          <TransporterDashboardModern />
        ) : state.activeDashboardTab === 'ftl' ? (
          <FtlMovementDashboardModern />
        ) : state.activeDashboardTab === 'inbound' ? (
          <InboundDashboardModern />
        ) : state.activeDashboardTab === 'pod' ? (
          <PodDashboardModern />
        ) : state.activeDashboardTab === 'co2' ? (
          <Co2DashboardModern />
        ) : (
          <>
        {state.error && <div className="pdm-error-banner">{state.error}</div>}

        <div className="pdm-filterbar">
          <div className="pdm-field">
            <label className="pdm-filter-required">From</label>
            <DatePicker className="pdm-datepicker" selected={f.startDate} onChange={(v) => this.onFilterChange('startDate', v)} dateFormat="dd/MM/yyyy" maxDate={f.endDate} />
          </div>
          <div className="pdm-field">
            <label className="pdm-filter-required">To</label>
            <DatePicker className="pdm-datepicker" selected={f.endDate} onChange={(v) => this.onFilterChange('endDate', v)} dateFormat="dd/MM/yyyy" minDate={f.startDate} maxDate={new Date()} />
          </div>
          <div className="pdm-field">
            <label>Date Basis</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.dateType} options={DATE_TYPE_OPTIONS} onChange={(v) => this.onFilterChange('dateType', v)} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Entity</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.entity} options={fo.entity} onChange={(v) => this.onFilterChange('entity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Mode</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.movementType} options={fo.movementType} onChange={(v) => this.onFilterChange('movementType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceZone} options={fo.sourceZone} onChange={(v) => this.onFilterChange('sourceZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceCity} options={fo.sourceCity} onChange={(v) => this.onFilterChange('sourceCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationZone} options={fo.destinationZone} onChange={(v) => this.onFilterChange('destinationZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Late Delivery Remarks</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.lateDeliveryRemarks} options={fo.lateDeliveryRemarks} onChange={(v) => this.onFilterChange('lateDeliveryRemarks', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter Category</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporterCategory} options={fo.transporterCategory} onChange={(v) => this.onFilterChange('transporterCategory', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Invoice Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.invoiceType} options={fo.invoiceType} onChange={(v) => this.onFilterChange('invoiceType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter / Courier</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporter} options={fo.transporter} onChange={(v) => this.onFilterChange('transporter', v || [])} />
          </div>
          <div className="pdm-field">
            <label>Tracking Mode (FTL/PTL)</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.trackingMode} options={TRACKING_MODE_OPTIONS} onChange={(v) => this.onFilterChange('trackingMode', v)} />
          </div>
          <div className="pdm-filter-toolbar">
            <button className="pdm-btn pdm-btn-ghost" onClick={this.onReset}>Reset</button>
            <button className="pdm-btn pdm-btn-primary" onClick={this.onApply}>Apply Filters</button>
          </div>
        </div>

        {!d ? (
          <div className="pdm-card">{this.renderEmptyState('Loading dashboard...', null)}</div>
        ) : d.deliveredTotal === 0 ? (
          <div className="pdm-card">{this.renderEmptyState('No delivered shipments in range', 'Widen the date range or clear a filter to see results.')}</div>
        ) : (
          <>
            <div className="pdm-kpi-grid">
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Delivered Shipments</div>
                <div className="pdm-kpi-value">{d.deliveredTotal}</div>
                <div className="pdm-kpi-sub">{d.ontimeCount} on-time / {d.delayCount} delayed</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Avg. Delivery Days</div>
                <div className="pdm-kpi-value">{d.avgDeliveryDays}</div>
                <div className="pdm-kpi-sub">Dispatch to delivery</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Total Shipment Records</div>
                <div className="pdm-kpi-value">{d.totalLRs}</div>
                <div className="pdm-kpi-sub">All statuses, current filters</div>
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                {this.buildGaugeCard(d.ontimePercent)}
                {this.renderChartCard('Month-wise On-Time Delivery Performance', 'Trend across the selected date range.', this.buildMonthlyTrendOptions(d.monthlyTrend))}
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head"><div><h2>On-Time Delivery Performance</h2></div></div>
              <div className="pdm-grid-2">
                {this.renderChartCard('Origin City', 'Vertical bar, on-time % by source city.', this.buildVerticalPercentBar(d.originCity, 'On-Time %'), 320, chartScrollWidth(d.originCity))}
                <div className="pdm-card">
                  <div className="pdm-card-toolbar" style={{ justifyContent: 'space-between' }}>
                    <p className="pdm-card-title" style={{ margin: 0 }}>Destination Zone / State / City</p>
                    <div className="pdm-mode-toggle">
                      {DEST_DIMENSIONS.map((dim) => (
                        <button key={dim.key} className={`pdm-chip ${state.destGroupBy === dim.key ? 'pdm-chip-active' : ''}`} onClick={() => this.setState({ destGroupBy: dim.key })}>{dim.label}</button>
                      ))}
                    </div>
                  </div>
                  <p className="pdm-card-subtitle">Vertical bar, sorted descending by on-time %.</p>
                  {d.destinationByDimension[state.destGroupBy].length ? (
                    <div className={`pdm-chart-flex ${chartScrollWidth(d.destinationByDimension[state.destGroupBy]) ? 'pdm-chart-scroll' : ''}`} style={{ height: 320 }}>
                      <div style={{ height: '100%', minWidth: chartScrollWidth(d.destinationByDimension[state.destGroupBy]) || undefined }}>
                        <HighchartsReact
                          highcharts={Highcharts}
                          containerProps={{ style: { width: '100%', height: '100%' } }}
                          options={{ ...this.buildVerticalPercentBar(d.destinationByDimension[state.destGroupBy], 'On-Time %'), chart: { ...baseChartOptions().chart, type: 'column', height: 320 } }}
                          updateArgs={[true, true, true]}
                        />
                      </div>
                    </div>
                  ) : this.renderEmptyState('No data available', null)}
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                {this.renderChartCard('Transporter', 'Vertical bar, sorted descending by on-time %.', this.buildVerticalPercentBar(d.transporterChart, 'On-Time %'), 380, chartScrollWidth(d.transporterChart))}
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                {this.renderChartCard('Breach Days Distribution', 'Share of delivered shipments in each delay bucket.', this.buildBreachChartOptions(d.breachBuckets), 500)}
                <GridCard title="On-Time Delivery Performance (Transporter - Month Wise)" loading={state.loading} onRefresh={this.loadData} height={500} {...buildTransporterMonthGrid(d)} />
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                <GridCard
                  title="Transporter-Wise Average Delivery Performance Days (State Wise)"
                  loading={state.loading}
                  onRefresh={this.loadData}
                  footerNote="Needs both a dispatch date and an actual delivery date on the shipment."
                  {...buildAvgDaysGrid(d)}
                />
                <GridCard title="Transporter-Wise LR's Delivered" loading={state.loading} onRefresh={this.loadData} {...buildLrDeliveredGrid(d)} />
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                <GridCard
                  title="Performance Category Wise Performance"
                  loading={state.loading}
                  onRefresh={this.loadData}
                  footerNote="Performance Category is sourced from Late Delivery Remarks (the closest real field to a delay-reason category in this system)."
                  {...buildPerformanceCategoryGrid(d)}
                />
                <GridCard title="Transporter-Wise Performance Category (%)" loading={state.loading} onRefresh={this.loadData} {...buildTransporterCategoryGrid(d)} />
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head"><div><h2>CPD vs ETA Adherence</h2></div></div>
              <div className="pdm-card">
                <p className="pdm-card-subtitle">CPD Adherence = delivered on or before the Customer Promise Date (final_eta_dt). ETA Adherence = delivered on-time per the standard measure. Only rows with a valid CPD and delivery date count toward the CPD line.</p>
                {this.renderChartCard(null, null, this.buildCpdEtaAdherenceTrendOptions(d.monthlyTrend, d.cpdMonthlyTrend))}
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                <GridCard title="Route-Wise CPD vs ETA Adherence" loading={state.loading} onRefresh={this.loadData} height={480} {...buildRouteAdherenceGrid(d)} />
                <GridCard title="CPD vs ETA (Transporter Wise)" loading={state.loading} onRefresh={this.loadData} height={480} {...buildCpdEtaTransporterGrid(d)} />
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Shipment Level Dump</h2>
                  <p>Full LR-wise detail for the selected filters. POD Aging column shows "—" - pending backend data.</p>
                </div>
              </div>
              <GridCard
                title="Shipment Level Dump"
                loading={state.loading}
                onRefresh={this.loadData}
                height={600}
                records={state.filteredRows}
                columnDefs={this.shipmentDumpColumnDefs}
              />
            </div>
          </>
        )}
          </>
        )}
      </div>
    );
  }
}
